/// <amd-module name="TFS/Admin/Groups/Pivots/Permissions/Redux/PermissionsPivotActions" />
import { createAction, ActionsUnion } from "redux/Utils/ActionHelper";
import { SubjectPermissions, SubjectPermission, PermissionTrace, PermissionValue } from "TFS/Admin/Common/Contracts/OrganizationSettings";
import { PermissionsOperation } from "TFS/Admin/Groups/Common/Constants";
import { MessageCardSeverity } from "VSSUI/MessageCard";
import { IdentityDescriptor } from "VSS/Platform/Contracts/Identities";

/**
 * Actions for Users view
 */
export const PermissionsPivotActions = {
    initView: (subjectDescriptor: string, subjectName: string) =>
        createAction(PermissionsPivotActionTypes.InitView, { subjectDescriptor, subjectName }),
    disposeView: () => createAction(PermissionsPivotActionTypes.DisposeView),

    initialPermissionsLoadSucceeded: (permissions: SubjectPermissions) =>
        createAction(PermissionsPivotActionTypes.InitialPermissionsLoadSucceeded, { permissions }),
    initialPermissionsLoadFailed: () => createAction(PermissionsPivotActionTypes.InitialPermissionsLoadFailed),

    tracePermissions: (subjectDescriptor: string, permissions: SubjectPermissions) =>
        createAction(PermissionsPivotActionTypes.TracePermissions, { subjectDescriptor, permissions }),
    tracePermissionsSucceeded: (permissions: SubjectPermission[], traces: PermissionTrace[]) =>
        createAction(PermissionsPivotActionTypes.TracePermissionsSucceeded, { permissions, traces }),
    tracePermissionsFailed: () => createAction(PermissionsPivotActionTypes.TracePermissionsFailed),
    updateSingleTracePermissionSucceeded: (permission: SubjectPermission, trace: PermissionTrace) =>
        createAction(PermissionsPivotActionTypes.updateSingleTracePermissionSucceeded, { permission, trace }),

    permissionOperationStatus: (operation: PermissionsOperation, resultMessage: string, severity: MessageCardSeverity) =>
        createAction(PermissionsPivotActionTypes.PermissionOperationStatus, { operation, resultMessage, severity }),

    updatePermission: (
        namespaceId: string,
        token: string,
        permissionBit: number,
        newValue: PermissionValue,
        newPermissionInherited?: boolean,
        newPermissionGranted?: boolean,
        isProjectScope?: boolean
    ) =>
        createAction(PermissionsPivotActionTypes.UpdatePermission, {
            namespaceId,
            token,
            permissionBit,
            newValue,
            newPermissionInherited,
            newPermissionGranted,
            isProjectScope
        }),
    updatePermissionFailed: (namespaceId: string, permissionBit: number) =>
        createAction(PermissionsPivotActionTypes.UpdatePermissionFailed, { namespaceId, permissionBit }),

    setPermission: (
        identityDescriptor: IdentityDescriptor,
        updates: { namespaceId: string; token: string; permissionValue: PermissionValue; permissionBit: number },
        isProjectScope?: boolean
    ) => createAction(PermissionsPivotActionTypes.SetPermission, { identityDescriptor, updates, isProjectScope }),
    removePermission: (
        identityDescriptor: string,
        subjectDescriptor: string,
        namespaceId: string,
        permissionsBit: number,
        token: string,
        isProjectScope?: boolean
    ) =>
        createAction(PermissionsPivotActionTypes.RemovePermission, {
            identityDescriptor,
            subjectDescriptor,
            namespaceId,
            permissionsBit,
            token,
            isProjectScope
        }),
    pickListActive: (permission: SubjectPermission) => createAction(PermissionsPivotActionTypes.PickListActive, permission),

    selectPermission: (permission: SubjectPermission) => createAction(PermissionsPivotActionTypes.SelectPermission, permission),
    onDismissMessageCard: () => createAction(PermissionsPivotActionTypes.DismissMessageCard)
};

export const enum PermissionsPivotActionTypes {
    InitView = "PermissionsPivot/Init",
    DisposeView = "PermissionsPivot/Dispose",
    InitialPermissionsLoadSucceeded = "PermissionsPivot/InitialPermissionsLoadSucceeded",
    InitialPermissionsLoadFailed = "PermissionsPivot/InitialPermissionsLoadFailed",
    TracePermissions = "PermissionsPivot/TracePermissions",
    TracePermissionsSucceeded = "PermissionsPivot/TracePermissionsSucceeded",
    TracePermissionsFailed = "PermissionsPivot/TracePermissionsFailed",
    updateSingleTracePermissionSucceeded = "PermissionsPivot/updateSingleTracePermissionSucceeded",
    UpdatePermission = "PermissionsPivot/UpdatePermission",
    UpdatePermissionFailed = "PermissionsPivot/UpdatePermissionFailed",
    SetPermission = "PermissionsPivot/SetPermission",
    RemovePermission = "PermissionsPivot/RemovePermission",
    SelectPermission = "PermissionsPivot/SelectPermission",
    PickListActive = "PermissionsPivot/PickListActive",
    PermissionOperationStatus = "PermissionsPivot/PermissionOperationStatus",
    DismissMessageCard = "PermissionsPivot/DismissMessageCard"
}

export type PermissionsPivotActions = ActionsUnion<typeof PermissionsPivotActions>;
